import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Box, Container, Typography, Grid, Card, Button, Chip, Breadcrumbs, Link, Fade, Dialog, DialogTitle, DialogContent, IconButton, Divider, Avatar, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckCircle, People, Close, Star, Home, NavigateNext, TrendingUp, School, EmojiEvents, AccessTime, Palette, Restaurant, Public, SportsEsports, MusicNote, TheaterComedy } from '@mui/icons-material';
import ContactUsForm from '../components/ContactForm';
import course4 from '../assets/images/Courses4.webp';
import banner03 from '../assets/images/banner02.webp';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

const immersionProgramsData = {
  id: 'immersion-programs',
  image: course4,
  title: 'Immersion Programs',
  subtitle: 'Intensive Cultural & Language Experience',
  description: 'Experience Chinese language and culture through our immersive holiday programs. Combine intensive language learning with cultural workshops for a comprehensive educational experience.',
  detailedDescription: [
    'Our Immersion Programs offer a unique blend of intensive Chinese language learning and authentic cultural experiences. Designed for learners who want to dive deep into Chinese culture while rapidly improving their language skills, these programs provide an unparalleled educational journey.',
    'Each program is carefully crafted to provide 6 hours of daily engagement through a perfect balance of structured language training and hands-on cultural activities. Students not only learn the language but also gain practical insights into Chinese traditions, arts, and contemporary culture.',
    'Perfect for holiday learners, cultural enthusiasts, and anyone seeking an intensive learning experience, our immersion programs cater to all age groups and proficiency levels. Whether you\'re a complete beginner or looking to enhance your existing skills, our programs adapt to your learning needs.'
  ],
  features: [
    '3 Hours Daily Language Training',
    '3 Hours Daily Cultural Workshops',
    'Traditional Chinese Arts & Crafts',
    'Cultural Field Trips & Activities',
    'Interactive Cultural Games',
    'Traditional Chinese Cooking',
    'Calligraphy & Painting',
    'Cultural Performance Training'
  ],
  learningOutcomes: [
    'Develop conversational fluency in practical daily Chinese',
    'Gain deep understanding of Chinese cultural traditions and practices',
    'Master traditional Chinese arts including calligraphy and painting',
    'Experience authentic Chinese cuisine through hands-on cooking classes',
    'Build confidence in cross-cultural communication',
    'Create lasting memories through cultural performances and activities',
    'Develop appreciation for Chinese history and contemporary culture',
    'Form connections with fellow learners from diverse backgrounds'
  ],
  pricing: {
    online: 'Not Available',
    offline: '$500/day',
    duration: '6 hours per day',
    groupSize: 'Small groups (4-12 participants)',
    groupDiscount: 'Group booking: 5% discount for 3+ participants',
    package: '10-day program: $4,500 (10% discount)'
  },
  targetAudience: 'All Ages, Cultural Enthusiasts, Holiday Learners, Students',
  level: 'All Levels (Beginner to Advanced)',
  programStructure: [
    {
      level: 'Foundation Program',
      duration: '5-7 days',
      focus: 'Basic language skills and cultural introduction',
      topics: [
        'Essential Chinese phrases and greetings',
        'Introduction to Chinese characters and pinyin',
        'Basic calligraphy and brush painting',
        'Traditional tea ceremony experience',
        'Simple Chinese cooking lessons',
        'Cultural games and storytelling'
      ]
    },
    {
      level: 'Intermediate Program',
      duration: '10-14 days',
      focus: 'Conversational skills and deeper cultural exploration',
      topics: [
        'Daily conversation and practical communication',
        'Chinese history and cultural traditions',
        'Advanced calligraphy and painting techniques',
        'Traditional Chinese medicine introduction',
        'Regional cuisine cooking workshops',
        'Cultural performance preparation'
      ]
    },
    {
      level: 'Advanced Program',
      duration: '14-21 days',
      focus: 'Cultural mastery and language fluency',
      topics: [
        'Business and professional communication',
        'Chinese philosophy and literature',
        'Master-level arts and crafts workshops',
        'Cultural field trips and excursions',
        'Advanced cooking and food culture',
        'Cultural presentation and performance'
      ]
    }
  ],
  dailySchedule: [
    {
      time: '9:00 AM - 12:00 PM',
      activity: 'Language Training Session',
      description: 'Intensive Chinese language instruction focusing on speaking, listening, and practical communication skills'
    },
    {
      time: '12:00 PM - 1:00 PM',
      activity: 'Lunch Break',
      description: 'Enjoy authentic Chinese cuisine while practicing conversational Chinese'
    },
    {
      time: '1:00 PM - 4:00 PM',
      activity: 'Cultural Workshop',
      description: 'Hands-on cultural activities including arts, crafts, cooking, or cultural excursions'
    },
    {
      time: '4:00 PM - 4:30 PM',
      activity: 'Cultural Tea Time',
      description: 'Traditional tea ceremony and cultural discussion'
    }
  ],
  culturalActivities: [
    {
      title: 'Traditional Chinese Calligraphy',
      description: 'Learn the art of Chinese brush writing and create your own masterpieces',
      icon: <Palette />
    },
    {
      title: 'Chinese Cooking Classes',
      description: 'Master authentic Chinese recipes and cooking techniques',
      icon: <Restaurant />
    },
    {
      title: 'Cultural Field Trips',
      description: 'Visit Chinese cultural sites and experience traditions firsthand',
      icon: <Public />
    },
    {
      title: 'Interactive Cultural Games',
      description: 'Learn through traditional Chinese games and activities',
      icon: <SportsEsports />
    },
    {
      title: 'Music and Performance',
      description: 'Experience Chinese music, dance, and theatrical traditions',
      icon: <MusicNote />
    },
    {
      title: 'Arts and Crafts Workshops',
      description: 'Create traditional Chinese crafts and artistic works',
      icon: <TheaterComedy />
    }
  ],
  whyChooseUs: [
    'Native Chinese instructors with cultural expertise',
    'Small group sizes for personalized attention',
    'Authentic cultural experiences and materials',
    'Flexible program duration and customization',
  ],
  faq: [
    {
      question: 'What is included in the immersion program fee?',
      answer: 'The program fee includes all language instruction, cultural workshops, materials, lunch, cultural activities, and a certificate of completion. Accommodation and dinner are not included but can be arranged separately.'
    },
    {
      question: 'Do I need prior Chinese language experience?',
      answer: 'No prior experience is required. Our programs are designed for all levels, from complete beginners to advanced learners. We assess participants on the first day to ensure appropriate grouping.'
    },
    {
      question: 'What is the minimum age for participation?',
      answer: 'We welcome participants of all ages. Children under 12 must be accompanied by a parent or guardian. We offer special family programs for parents and children learning together.'
    },
    {
      question: 'How many participants are in each group?',
      answer: 'We maintain small group sizes of 4-12 participants to ensure personalized attention and meaningful cultural exchange between participants from different backgrounds.'
    },
    {
      question: 'Can the program be customized for specific interests?',
      answer: 'Yes! We offer customized programs focusing on specific aspects of Chinese culture such as business culture, traditional arts, cuisine, or history. Contact us to discuss your specific interests.'
    },
    {
      question: 'What cultural activities are included?',
      answer: 'Activities vary by program but typically include calligraphy, painting, cooking, tea ceremony, traditional games, cultural performances, and field trips to cultural sites.'
    }
  ]
};

function ImmersionPrograms() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [visibleSections, setVisibleSections] = useState({});
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = ['overview', 'structure', 'schedule', 'activities', 'why-choose-us', 'pricing', 'faq'];
    sections.forEach(section => {
      const element = document.getElementById(section);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SEOHead pageKey="immersion-programs" />
      <Box sx={{ 
        minHeight: '100vh', 
        position: 'relative',
      }}>

      {/* Header Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '240px', md: '500px' },
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Background Image with Overlay */}
        <Box
          component="img"
          src={banner03}
          alt="Immersion Programs Banner"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
        />
        
        {/* Content */}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            minHeight: { xs: '240px', md: '500px' },
            pt: 0,
            px: { xs: 1, md: 4 }
          }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12}>
                <Box sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 60,
                    left: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    zIndex: -1
                  }
                }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '1.5rem',
                      lineHeight: 1.6,
                      letterSpacing: '0.1em',
                      mb: 4,
                      fontWeight: 500,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      display: { xs: 'none', md: 'block' }
                    }}
                  >
                    Immerse yourself in authentic Chinese culture and language
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: 'serif',
                      fontWeight: 700,
                      color: 'white',
                      mb: 5,
                      fontSize: { xs: '2.25rem', md: '4rem' },
                      lineHeight: 1.1,
                      textShadow: '2px 2px 8px rgba(0,0,0,0.7)'
                    }}
                  >
                    Immersion Programs
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setEnrollDialogOpen(true)}
                      sx={{
                        bgcolor: '#9f2122',
                        color: 'white',
                        px: 6,
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 10,
                        textTransform: 'none',
                        boxShadow: '0 4px 20px rgba(159, 33, 34, 0.4)',
                        '&:hover': {
                          bgcolor: '#8a1e1f',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 25px rgba(159, 33, 34, 0.5)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Join Our Program
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Breadcrumb Navigation */}
      <Box sx={{ 
        bgcolor: 'white', 
        py: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <Container maxWidth="xl">
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                color: '#5a6c7d'
              }
            }}
          >
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center'}}
              href="/"
            >
              <Home sx={{ mr: 0.5 }} fontSize="inherit" />
              Home
            </Link>
            <Link
              underline="hover"
              href="/courses"
            >
              Courses
            </Link>
            <Typography>
              Immersion Programs
            </Typography>
          </Breadcrumbs>
        </Container>
      </Box>

      {/* Course Overview Section */}
      <Box id="overview" sx={{ py: { xs: 6, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Fade in={visibleSections['overview']} timeout={800}>
            <Grid container spacing={ { xs: 4, md: 8 }}>
              <Grid item xs={12} md={7}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontFamily: 'serif',
                      fontWeight: 700,
                      color: '#07284b',
                      mb: 3,
                      fontSize: { xs: '2rem', md: '2.5rem' }
                    }}
                  >
                    {immersionProgramsData.title}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#9f2122',
                      mb: { xs: 2, md: 4 },
                      fontWeight: 600,
                      fontSize: { xs: '1.2rem', md: '1.5rem' }
                    }}
                  >
                    {immersionProgramsData.subtitle}
                  </Typography>
                  
                  <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<People />}
                      label={`Target: ${immersionProgramsData.targetAudience}`}
                      sx={{
                        bgcolor: 'rgba(7, 40, 75, 0.1)',
                        color: '#07284b',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    />
                    <Chip
                      icon={<Star />}
                      label={`Level: ${immersionProgramsData.level}`}
                      sx={{
                        bgcolor: 'rgba(7, 40, 75, 0.1)',
                        color: '#07284b',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      mb: { xs: 2, md: 4 },
                      lineHeight: 1.8,
                      fontSize: '1.1rem'
                    }}
                  >
                    {immersionProgramsData.description}
                  </Typography>
                  
                  {immersionProgramsData.detailedDescription.map((paragraph, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{
                        color: '#5a6c7d',
                        mb: { xs: 2, md: 3 },
                        lineHeight: 1.8,
                        fontSize: '1.1rem'
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box
                  component="img"
                  src={immersionProgramsData.image}
                  alt="Immersion Programs Course"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Program Structure */}
      <Box id="structure" sx={{ py: { xs: 6, md: 12 }, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Program Structure
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              Flexible programs designed to match your learning goals and schedule
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {immersionProgramsData.programStructure.map((program, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: 'none',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: index === 0 
                        ? 'linear-gradient(90deg, #3b82f6, #1d4ed8)'
                        : index === 1 
                        ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                        : 'linear-gradient(90deg, #10b981, #059669)',
                      zIndex: 1
                    },
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
                      '& .program-icon': {
                        transform: 'scale(1.1) rotate(5deg)'
                      }
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2, md: 4 },
                      background: index === 0 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                        : index === 1 
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        zIndex: 0
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          className="program-icon"
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            mr: 2,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {index === 0 ? <School /> : index === 1 ? <TrendingUp /> : <EmojiEvents />}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              mb: 0.5,
                              fontSize: '1.4rem',
                              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                          >
                            {program.level}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.9rem',
                              fontWeight: 500
                            }}
                          >
                            Duration: {program.duration}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ p: 4, bgcolor: 'white' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        mb: 3,
                        fontSize: '1.1rem',
                        lineHeight: 1.4
                      }}
                    >
                      Focus Areas
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        mb: 3,
                        fontSize: '0.95rem',
                        lineHeight: 1.5
                      }}
                    >
                      {program.focus}
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: '#e2e8f0' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Key Activities
                    </Typography>
                    <Box>
                      {program.topics.map((topic, topicIndex) => (
                        <Box key={topicIndex} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: index === 0 ? '#3b82f6' : index === 1 ? '#f59e0b' : '#10b981',
                              mt: 0.5,
                              mr: 2,
                              flexShrink: 0,
                              boxShadow: `0 0 0 2px ${index === 0 ? 'rgba(59, 130, 246, 0.2)' : index === 1 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#64748b',
                              fontSize: '0.9rem',
                              lineHeight: 1.5
                            }}
                          >
                            {topic}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Daily Schedule */}
      <Box id="schedule" sx={{ py: { xs: 6, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Daily Schedule
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              A typical day in our immersion program
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {immersionProgramsData.dailySchedule.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      borderColor: '#9f2122'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#9f2122',
                        mr: 2
                      }}
                    >
                      <AccessTime />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#07284b',
                          fontWeight: 700,
                          fontSize: '1.2rem',
                          mb: 0.5
                        }}
                      >
                        {item.time}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: '#9f2122',
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                      >
                        {item.activity}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6,
                      fontSize: '1rem'
                    }}
                  >
                    {item.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Cultural Activities */}
      <Box id="activities" sx={{ 
        py: { xs: 6, md: 12 }, 
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(7, 40, 75, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(159, 33, 34, 0.03) 0%, transparent 50%)',
          pointerEvents: 'none'
        }
      }}>
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Cultural Activities & Workshops
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.2rem',
                fontWeight: 500
              }}
            >
              Experience authentic Chinese culture through hands-on activities and workshops
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {immersionProgramsData.culturalActivities.map((activity, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: 'none',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: index % 2 === 0 
                        ? 'linear-gradient(90deg, #07284b, #1e4a7c)'
                        : 'linear-gradient(90deg, #9f2122, #dc2626)',
                      zIndex: 1
                    },
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
                      '& .activity-icon': {
                        transform: 'scale(1.1) rotate(5deg)'
                      }
                    }
                  }}
                >
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <Avatar
                        className="activity-icon"
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: index % 2 === 0 ? '#07284b' : '#9f2122',
                          background: index % 2 === 0 
                            ? 'linear-gradient(135deg, #07284b, #1e4a7c)'
                            : 'linear-gradient(135deg, #9f2122, #dc2626)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {activity.icon}
                      </Avatar>
                    </Box>
                    
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '1.2rem',
                        lineHeight: 1.4
                      }}
                    >
                      {activity.title}
                    </Typography>
                    
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '0.95rem',
                        lineHeight: 1.5
                      }}
                    >
                      {activity.description}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Learning Outcomes */}
      <Box sx={{ py: { xs: 6, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Learning Outcomes
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              What you will achieve through our immersion programs
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            {immersionProgramsData.learningOutcomes.map((outcome, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 0, md: 2 } }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#9f2122',
                      mt: 1,
                      mr: 3,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6,
                      fontSize: '1.1rem'
                    }}
                  >
                    {outcome}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box id="why-choose-us" sx={{ 
        py: { xs: 6, md: 12 }, 
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Why Choose Our Immersion Programs?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.2rem',
                fontWeight: 500
              }}
            >
              Experience the difference with our comprehensive approach to Chinese language and culture
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {immersionProgramsData.whyChooseUs.map((reason, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: 'none',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: index % 2 === 0 
                        ? 'linear-gradient(90deg, #07284b, #1e4a7c)'
                        : 'linear-gradient(90deg, #9f2122, #dc2626)',
                      zIndex: 1
                    },
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
                      '& .reason-icon': {
                        transform: 'scale(1.1) rotate(5deg)'
                      }
                    }
                  }}
                >
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <Avatar
                        className="reason-icon"
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: index % 2 === 0 ? '#07284b' : '#9f2122',
                          background: index % 2 === 0 
                            ? 'linear-gradient(135deg, #07284b, #1e4a7c)'
                            : 'linear-gradient(135deg, #9f2122, #dc2626)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <CheckCircle sx={{ fontSize: '1.8rem', color: 'white' }} />
                      </Avatar>
                    </Box>
                    
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1e293b',
                        fontWeight: 600,
                        mb: 2,
                        fontSize: '1.1rem',
                        lineHeight: 1.4
                      }}
                    >
                      {reason}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ 
        py: { xs: 6, md: 8 }, 
        bgcolor: 'white'
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3rem' }
              }}
            >
              Program Options
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Choose the immersion program that best fits your schedule and learning goals
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {/* Daily Program */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px solid rgba(7, 40, 75, 0.1)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    borderColor: '#07284b'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: '#07284b',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.5rem', md: '1.8rem' }
                  }}
                >
                  Daily Program
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    fontSize: '1rem'
                  }}
                >
                  $500 per day
                </Typography>
                
                <List>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="6 hours of intensive learning" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="3 hours language + 3 hours culture" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="All materials included" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Lunch and cultural tea time" />
                  </ListItem>
                </List>
              </Card>
            </Grid>

            {/* Package Program */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 4,
                  border: '2px solid #9f2122',
                  position: 'relative',
                  bgcolor: 'rgba(159, 33, 34, 0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(159, 33, 34, 0.2)'
                  }
                }}
              >
                <Chip
                  label="Best Value"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: '#9f2122',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                  
                <Typography
                  variant="h4"
                  sx={{
                    color: '#07284b',
                    fontWeight: 700,
                    mb: 1,
                    mt: 2,
                    fontSize: { xs: '1.5rem', md: '1.8rem' }
                  }}
                >
                  10-Day Package
                </Typography>
                  
                <Typography
                  variant="body1"
                  sx={{
                    color: '#5a6c7d',
                    mb: 3,
                    fontSize: '1rem'
                  }}
                >
                  $4,500 (Save $500!)
                </Typography>
                
                <List>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="10% discount on daily rate" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Comprehensive cultural experience" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Certificate of completion" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle sx={{ color: '#9f2122', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary="Cultural performance opportunity" />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>

          {/* Special Offers */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Card
              sx={{
                p: 4,
                background: 'linear-gradient(135deg, rgba(7, 40, 75, 0.05) 0%, rgba(159, 33, 34, 0.03) 100%)',
                border: '1px solid rgba(159, 33, 34, 0.2)',
                borderRadius: 3,
                maxWidth: 800,
                mx: 'auto'
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#07284b',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Special Program Features
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Chip
                    label="All materials & lunch included"
                    sx={{
                      bgcolor: '#9f2122',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                      px: 1
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Chip
                    label="Cultural certificate upon completion"
                    sx={{
                      bgcolor: '#07284b',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                      px: 1
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ py: { xs: 6, md: 12 }, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                color: '#07284b',
                mb: 3,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#5a6c7d',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: '1.1rem'
              }}
            >
              Find answers to common questions about our Immersion Programs
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {immersionProgramsData.faq.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'white',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      borderColor: '#9f2122'
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#07284b',
                      fontWeight: 700,
                      mb: 3,
                      fontSize: '1.2rem'
                    }}
                  >
                    Q: {item.question}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6,
                      fontSize: '1.1rem'
                    }}
                  >
                    A: {item.answer}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 6, md: 12 },
          background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Ready to Immerse Yourself in Chinese Culture?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Join our immersive programs and discover the richness of Chinese language and culture
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setEnrollDialogOpen(true)}
              sx={{
                bgcolor: 'white',
                color: '#07284b',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Join Our Program
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Contact Form Dialog */}
      <Dialog
        open={enrollDialogOpen}
        onClose={() => setEnrollDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallScreen}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: {xs: 2, md: 4},
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: 'serif',
              fontWeight: 700,
              fontSize: { xs: '1.3rem', md: '1.5rem' }
            }}
          >
            Join Our Immersion Program
          </Typography>
          <IconButton
            onClick={() => setEnrollDialogOpen(false)}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#9f2122',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#8a1e1f'
            }
          }}
        >
            <Box sx={{ px: 4, py: { xs: 4, md: 8 } }}>
              <ContactUsForm />
            </Box>
        </DialogContent>
      </Dialog>
      </Box>
    </>
   );
}

export default ImmersionPrograms;
